import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Card, Col, Descriptions, Row, Space, Table, Tag, Typography, Button } from "antd";
import { Link } from "react-router";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { TARIFF_LABEL, TARIFF_COLOR, type Tariff } from "../../types";
import {
  DEVICE_LABEL, OS_LABEL, CLIENT_LABEL,
  type OnboardingDevice, type OnboardingOS, type OnboardingClient,
} from "../../types";
import { fmtRub, fmtDateTime, fmtUserName } from "../../utils";

interface UserDetail {
  tg_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  lang: string | null;
  flagged_abuse: boolean;
  bot_blocked: boolean;
  account_deleted: boolean;
  admin_notes: string | null;
  referred_by: number | null;
  referred_at: string | null;
  created_at: string | null;
  last_seen_at: string | null;
  device_type: OnboardingDevice | null;
  device_os: OnboardingOS | null;
  vpn_client: OnboardingClient | null;
  onboarding_step: number;
  onboarding_completed_at: string | null;
  subscriptions: {
    id: number; tariff: Tariff; label: string | null;
    sub_url: string; duration_months: number;
    start_at: string | null; end_at: string | null;
    is_active: boolean; device_limit: number;
    uuid_reset_count: number; hysteria_revoked_at: string | null;
  }[];
  payments: {
    id: number; gateway: string; final_rub: number;
    status: string; created_at: string | null; paid_at: string | null;
    cart: any[];
  }[];
  referrals: {
    id: number; referred_id: number;
    referred_at: string | null; status: string;
    reward_choice: string | null;
  }[];
}

export const UserShow = () => {
  const { query } = useShow<UserDetail>();
  const u = query.data?.data;
  const navigate = useNavigate();

  if (!u) return <Show isLoading={query.isLoading} />;

  const totalPaid = u.payments
    .filter(p => p.status === "paid")
    .reduce((s, p) => s + p.final_rub, 0);

  return (
    <Show
      isLoading={query.isLoading}
      title={`Юзер ${u.username ? "@" + u.username : "tg:" + u.tg_id}`}
      breadcrumb={false}
      headerButtons={() => (
        <Button type="primary" icon={<PlusCircleOutlined />}
                onClick={() => navigate(`/subscriptions/create?user_id=${u.tg_id}`)}>
          Создать UUID для этого юзера
        </Button>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Telegram ID">
              <Typography.Text code>{u.tg_id}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Username">
              {u.username ? `@${u.username}` : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Имя">{fmtUserName(u)}</Descriptions.Item>
            <Descriptions.Item label="Язык">{u.lang || "—"}</Descriptions.Item>
            <Descriptions.Item label="Регистрация">
              {fmtDateTime(u.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Был онлайн">
              {fmtDateTime(u.last_seen_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Реферер">
              {u.referred_by
                ? <Link to={`/users/${u.referred_by}`}>tg:{u.referred_by}</Link>
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Флаги">
              <Space size={4}>
                {u.flagged_abuse && <Tag color="red">⚠ flagged</Tag>}
                {u.bot_blocked && <Tag>🚫 заблокировал бот</Tag>}
                {u.account_deleted && <Tag color="default">удалён</Tag>}
                {!u.flagged_abuse && !u.bot_blocked && !u.account_deleted && "—"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Заметки админа" span={2}>
              {u.admin_notes || <span style={{ opacity: 0.4 }}>—</span>}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Анкета (онбординг)" size="small">
          {u.onboarding_step < 4 ? (
            <Space direction="vertical" size={4}>
              <Tag color={u.onboarding_step === 0 ? "default" : "orange"}>
                {u.onboarding_step === 0
                  ? "Анкета не начата"
                  : `В процессе — шаг ${u.onboarding_step} из 3`}
              </Tag>
              <Typography.Text type="secondary">
                Юзер пока не закончил анкету. До завершения бот блокирует
                остальные функции и просит ответить на вопросы.
              </Typography.Text>
            </Space>
          ) : (
            <Descriptions column={3} size="small">
              <Descriptions.Item label="Устройство">
                {u.device_type ? (
                  <Tag color="blue">{DEVICE_LABEL[u.device_type]}</Tag>
                ) : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Система">
                {u.device_os ? <Tag>{OS_LABEL[u.device_os]}</Tag> : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Приложение">
                {u.vpn_client ? (
                  <Tag color={u.vpn_client === "setup_help" ? "gold" : "purple"}>
                    {CLIENT_LABEL[u.vpn_client]}
                  </Tag>
                ) : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Анкета завершена" span={3}>
                {fmtDateTime(u.onboarding_completed_at)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Typography.Text type="secondary">Всего оплачено</Typography.Text>
              <Typography.Title level={3}>{fmtRub(totalPaid)}</Typography.Title>
              <Typography.Text type="secondary">
                {u.payments.length} платежей · {u.referrals.length} приведенных
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} md={16}>
            <Card title={`Подписки (${u.subscriptions.length})`}
                  size="small">
              <Table size="small" pagination={false} rowKey="id"
                     dataSource={u.subscriptions}
                     columns={[
                       { title: "ID", dataIndex: "id",
                         render: (id: number) => <Link to={`/subscriptions/${id}`}>#{id}</Link> },
                       { title: "Тариф", dataIndex: "tariff",
                         render: (t: Tariff) => <Tag color={TARIFF_COLOR[t]}>{TARIFF_LABEL[t]}</Tag> },
                       { title: "Ярлык", dataIndex: "label",
                         render: (v: string | null) => v || "—" },
                       { title: "До", dataIndex: "end_at",
                         render: (v: string | null) => v ? new Date(v).toLocaleDateString("ru-RU") : "—" },
                       { title: "Статус",
                         render: (_, r: any) => r.hysteria_revoked_at
                           ? <Tag color="red">🔒</Tag>
                           : r.is_active ? <Tag color="green">✅</Tag> : <Tag>⌛</Tag> },
                     ]} />
            </Card>
          </Col>
        </Row>

        <Card title={`Платежи (${u.payments.length})`}>
          <Table size="small" rowKey="id" pagination={{ pageSize: 10 }}
                 dataSource={u.payments}
                 columns={[
                   { title: "ID", dataIndex: "id" },
                   { title: "Шлюз", dataIndex: "gateway" },
                   { title: "Сумма", dataIndex: "final_rub",
                     render: (v: number) => fmtRub(v), align: "right" },
                   { title: "Статус", dataIndex: "status",
                     render: (s: string) => <Tag color={s === "paid" ? "green" : s === "pending" ? "blue" : "default"}>{s}</Tag> },
                   { title: "Создан", dataIndex: "created_at", render: fmtDateTime },
                   { title: "Оплачен", dataIndex: "paid_at", render: fmtDateTime },
                 ]} />
        </Card>
      </Space>
    </Show>
  );
};
